import {
  AllFeatures,
  AllPrograms,
  SubscriptionBundlePrice,
  SubscriptionBundle,
  AllContent,
  toProgramStateKind,
} from '../../common/bundle.types';
import {
  ProgramReadiness,
  ProgramStateKind,
} from '@client/new/pages/programs/model/programs.types';
import {
  isProgramNotReady,
  toNotReadyTooltip,
} from '../../common/readinessGating';
import { BundleItemType, BundleStatus, FeatureCode } from 'generated/graphql';
import {
  EditBundleContentVMDispatcher,
  EditBundleContentVMState,
} from './editBundleContent.vm';
import { trackEvent } from '@services/mixpanel';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faShareNodes } from '@fortawesome/pro-regular-svg-icons';

export type BundleCardMenuItem = {
  icon: IconProp;
  disabled: boolean;
  onClick?: () => void;
  tooltip?: string | string[];
};

export type BundleCardFreeTrial = {
  monthly: {
    checked: boolean;
  };
  annual: {
    checked: boolean;
  };
};

export type BundleCard = {
  name: string;
  bundleId: number;
  price: string;
  freeTrial?: BundleCardFreeTrial;
  platformFreeTrial?: boolean;
  tooltipText?: Array<string>;
  onClick?: () => void;
  testIdSuffix: string;
  submitting: boolean;
  draftMode: boolean;
  menu: Array<BundleCardMenuItem>;
};

export type BundlesCards = Array<BundleCard>;

export type BundleContent = {
  bundleId: number;
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
  tooltip?: string;
  offConfirmation?: { title: string; description: string };
};

export type BundleSectionRowStatus = {
  state: ProgramStateKind;
  readiness: ProgramReadiness | null;
};

export type BundleSectionList = Array<{
  name: string;
  bundleContent: Array<BundleContent>;
  /**
   * Optional status indicator — populated only for the Programs section so
   * the matrix can render the readiness pill alongside the row label.
   * Features and Extras rows omit it.
   */
  status?: BundleSectionRowStatus;
}>;

export type BundleTableSection = {
  name: string;
  open: boolean;
  list: BundleSectionList;
};

export type BundleTableHeader = {
  bundleName: Array<string>;
  section: {
    open: boolean;
    toggle: () => void;
  };
};

export type BundlesTable = {
  header: BundleTableHeader;
  programs: BundleTableSection;
  features: BundleTableSection;
  content: BundleTableSection;
  links: {
    extrasPath: string;
    programsPath: string;
  };
};

const FEATURE_OFF_CONFIRMATIONS: Partial<
  Record<FeatureCode, { title: string; description: string }>
> = {
  [FeatureCode.Community]: {
    title: 'Turning off Community?',
    description:
      'Your subscribers will no longer be able to interact with each other and discuss their progress, and you will be unable to facilitate these conversations.',
  },
  [FeatureCode.Dm]: {
    title: 'Turning off DM?',
    description:
      'Your subscribers will no longer be able to send you direct messages and reply to stories.',
  },
  [FeatureCode.Livestream]: {
    title: 'Turning off Livestream?',
    description:
      "You won't be able to stream your content directly to your subscribers.",
  },
};

export const toTableSections = (
  dispatch: EditBundleContentVMDispatcher,
  state: EditBundleContentVMState,
  subscriptionPrograms: AllPrograms,
  allFeatures: AllFeatures,
  allContent: AllContent,
  bundles: SubscriptionBundle[],
  links: BundlesTable['links'],
  isUnifiedProgramsEnabled: boolean,
  isPlatformBundleActiveCreator: boolean,
): BundlesTable => ({
  header: {
    bundleName: bundles.map((b) => b.title),
    section: {
      open: state.listSectionOpen,
      toggle: () => dispatch('toggleListSection'),
    },
  },
  links,
  programs: {
    open: state.programsSectionOpen,
    name: 'program',
    list: subscriptionPrograms.map((p) => {
      // Readiness gating (visual pill + disable-checkbox tooltip) is part
      // of the unified-programs experience; under the legacy experience
      // the readiness snapshot is invisible and must not disable any row.
      const notReady =
        isUnifiedProgramsEnabled && isProgramNotReady(p.readiness);
      const notReadyTooltip = notReady
        ? toNotReadyTooltip(p.readiness)
        : undefined;
      return {
        name: p.name,
        status: {
          state: toProgramStateKind(p),
          readiness: isUnifiedProgramsEnabled ? (p.readiness ?? null) : null,
        },
        bundleContent: bundles.map((b) => {
          const platformDisabled =
            (!isUnifiedProgramsEnabled && !b.isPlatformBundle) ||
            bundlesProgramDisabledState(
              b,
              p.id,
              bundles,
              isPlatformBundleActiveCreator,
              isUnifiedProgramsEnabled,
            );
          const platformTooltip = isUnifiedProgramsEnabled
            ? b.isPlatformBundle &&
              !isPlatformBundleActiveCreator &&
              bundlesProgramDisabledState(
                b,
                p.id,
                bundles,
                isPlatformBundleActiveCreator,
                isUnifiedProgramsEnabled,
              )
              ? 'Program must be added to Exclusive Offer first'
              : undefined
            : !b.isPlatformBundle || isPlatformBundleActiveCreator
              ? b.items
                  .filter((i) => i.itemType === BundleItemType.Program)
                  .some((i) => i.itemId === p.id)
                ? `To remove a program from your ${
                    b.isPlatformBundle ? 'Platform' : 'Exclusive'
                  } Access you need to change its state to "Archived" in your "Subscription" tab`
                : `To add a program to your ${
                    b.isPlatformBundle ? 'Platform' : 'Exclusive'
                  } Access you need to change its state to "Published" in your "Subscription" tab`
              : undefined;

          return {
            bundleId: b.id,
            checked: b.items
              .filter((i) => i.itemType === BundleItemType.Program)
              .some((i) => i.itemId === p.id),
            onChange: () =>
              dispatch('toggleBundleContent', {
                bundleId: b.id,
                itemId: p.id,
                type: BundleItemType.Program,
              }),
            disabled: platformDisabled || notReady,
            // Platform/exclusive tooltip wins when present (it explains a
            // higher-level rule); fall back to readiness tooltip when the
            // only reason the row is disabled is missing criteria.
            tooltip: platformTooltip ?? notReadyTooltip,
          };
        }),
      };
    }),
  },
  features: {
    open: state.featuresSectionsOpen,
    name: 'Other features',
    list: allFeatures.map((f) => ({
      name: f.name,
      bundleContent: bundles.map((b) => {
        const checked = b.items
          .filter((i) => i.itemType === BundleItemType.Feature)
          .some((i) => i.itemId === f.id);
        const platformBundleHasFeature = bundles
          .filter((bundle) => bundle.isPlatformBundle)
          .some((bundle) =>
            bundle.items.some(
              (i) => i.itemType === BundleItemType.Feature && i.itemId === f.id,
            ),
          );
        return {
          bundleId: b.id,
          disabled: premiumBundleFeatureDisabledState(b, f.id, bundles),
          checked,
          onChange: () =>
            dispatch('toggleBundleContent', {
              bundleId: b.id,
              itemId: f.id,
              type: BundleItemType.Feature,
            }),
          offConfirmation:
            checked && !b.isPlatformBundle && !platformBundleHasFeature
              ? FEATURE_OFF_CONFIRMATIONS[f.code]
              : undefined,
        };
      }),
    })),
  },
  content: {
    open: state.contentSectionsOpen,
    name: 'Extras',
    list: allContent.map((c) => ({
      name: c.name,
      bundleContent: bundles.map((b) => ({
        bundleId: b.id,
        disabled: premiumBundleContentDisabledState(b, c.id, bundles),
        checked: b.items.some((i) => i.contentId === c.id),
        onChange: () =>
          dispatch('toggleBundleContent', {
            bundleId: b.id,
            contentId: c.id,
            type: BundleItemType.Content,
          }),
      })),
    })),
  },
});

export const bundlesProgramDisabledState = (
  bundle: SubscriptionBundle,
  programId: number,
  bundles: SubscriptionBundle[],
  isPlatformBundleActiveCreator: boolean,
  isUnifiedProgramsEnabled: boolean,
) => {
  if (!bundle.isPlatformBundle) return false;

  if (
    !isUnifiedProgramsEnabled &&
    bundle.isPlatformBundle &&
    isPlatformBundleActiveCreator
  ) {
    return true;
  }

  if (
    isUnifiedProgramsEnabled &&
    bundle.isPlatformBundle &&
    isPlatformBundleActiveCreator
  ) {
    return false;
  }

  if (hasBundleProgram(bundle, programId)) return false;

  const exclusiveBundle = bundles.find((b) => !b.isPlatformBundle);

  if (!exclusiveBundle) return true;

  return !hasBundleProgram(exclusiveBundle, programId);
};

const hasBundleProgram = (bundle: SubscriptionBundle, programId: number) =>
  bundle.items.some(
    (item) =>
      item.itemType === BundleItemType.Program && item.itemId === programId,
  );

export const premiumBundleFeatureDisabledState = (
  bundle: SubscriptionBundle,
  featureId: number,
  bundles: SubscriptionBundle[],
) => {
  if (!bundle.isPlatformBundle) return false;

  if (
    bundle.items.some(
      (i) => i.itemType === BundleItemType.Feature && i.itemId === featureId,
    )
  )
    return false;

  const exclusiveBundle = bundles.find((b) => !b.isPlatformBundle);

  return (
    exclusiveBundle &&
    !exclusiveBundle.items.some(
      (i) => i.itemType === BundleItemType.Feature && i.itemId === featureId,
    )
  );
};

export const premiumBundleContentDisabledState = (
  bundle: SubscriptionBundle,
  contentId: string,
  bundles: SubscriptionBundle[],
) => {
  if (!bundle.isPlatformBundle) return false;

  if (
    bundle.items.some(
      (i) => i.itemType === BundleItemType.Content && i.contentId === contentId,
    )
  )
    return false;

  const exclusiveBundle = bundles.find((b) => !b.isPlatformBundle);

  return (
    exclusiveBundle &&
    !exclusiveBundle.items.some(
      (i) => i.itemType === BundleItemType.Content && i.contentId === contentId,
    )
  );
};

const bundleTooltipTextForActiveExclusiveBundle = [
  'These are the general Playbook users who have access to all creators’ content as part of their subscription. You can decide how much of yours you want to allow them to access.',
  "New users will not be able to purchase this bundle. Users who are already subscribed to Playbook's platform product will have access to this content.",
];

const bundleTooltipTextForActivePlatformBundle = [
  'Users who subscribed to the Playbook Platform will have access to the content in this subscription offer. If you have an Exclusive offer set up, new users will have access to that.',
];

export const toBundleCards = (
  bundles: SubscriptionBundle[],
  editExclusiveBundle: (bundle: SubscriptionBundle) => void,
  editPlatformBundle: (bundle: SubscriptionBundle) => void,
  submittingBundleIds: number[],
  openLinksModal: () => void,
  isPlatformBundleActiveCreator: boolean,
  platformFreeTrial: boolean,
): BundlesCards =>
  bundles.map((b) => ({
    name: b.title,
    bundleId: b.id,
    price: toPriceText(b.price),
    platformFreeTrial:
      isPlatformBundleActiveCreator && b.isPlatformBundle
        ? platformFreeTrial
        : undefined,
    tooltipText: !b.isPlatformBundle
      ? undefined
      : isPlatformBundleActiveCreator
        ? bundleTooltipTextForActivePlatformBundle
        : bundleTooltipTextForActiveExclusiveBundle,
    freeTrial:
      isPlatformBundleActiveCreator || b.isPlatformBundle
        ? undefined
        : {
            monthly: {
              checked: b.freeTrial.monthly,
            },
            annual: {
              checked: b.freeTrial.annual,
            },
          },
    onClick: () =>
      b.isPlatformBundle ? editPlatformBundle(b) : editExclusiveBundle(b),
    testIdSuffix: b.isPlatformBundle ? 'platform' : 'exclusive',
    submitting: submittingBundleIds.includes(b.id),
    draftMode: b.status === BundleStatus.Draft,
    menu: toBundleCardMenu(b, openLinksModal),
  }));

const toBundleCardMenu = (
  bundle: SubscriptionBundle,
  openLinksModal: () => void,
): Array<BundleCardMenuItem> => [
  {
    icon: faShareNodes,
    disabled: bundle.status !== BundleStatus.Published,
    onClick: openLinksModal,
  },
];

export const toPriceText = (price: SubscriptionBundlePrice): string => {
  if (price?.monthly && price?.annual)
    return `$ ${priceFormatter(price.monthly)} / month • $ ${priceFormatter(
      price.annual,
    )} / year`;
  if (price?.monthly) return `$ ${priceFormatter(price.monthly)} / month`;
  if (price?.annual) return `$ ${priceFormatter(price.annual)} / year`;
  return '';
};

const priceFormatter = (price: number) =>
  new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(price);

export type HelpEventType =
  | 'chatSupport'
  | 'pricingArticle'
  | 'joinCommunity'
  | 'pricingVideo';

export const toHelpMixpanelEvent = (type: HelpEventType) => {
  switch (type) {
    case 'chatSupport':
      trackEvent({
        type: 'Offer Exclusive Help',
        payload: {
          chatRequested: true,
          url: '',
          urlTitle: '',
        },
      });
      return;
    case 'pricingArticle':
      trackEvent({
        type: 'Offer Exclusive Help',
        payload: {
          chatRequested: false,
          url: 'https://help.playbookapp.io/en/articles/13621806-how-to-turn-on-an-exclusive-subscription',
          urlTitle: 'How to Turn On an Exclusive Subscription',
        },
      });
      return;
    case 'joinCommunity':
      trackEvent({
        type: 'Offer Exclusive Help',
        payload: {
          chatRequested: false,
          url: 'https://community.joinplaybook.com/c/start-here/',
          urlTitle: 'Creator Community',
        },
      });
      return;
    case 'pricingVideo':
      trackEvent({
        type: 'Offer Exclusive Help',
        payload: {
          chatRequested: false,
          url: 'https://www.loom.com/share/011658005a5c4cb0ad3cd6fb6792b95c?sid=e416b75e-43ff-4e81-b427-cb0b71e36868',
          urlTitle: 'Platform: Pricing Your Subscriptions & Discounts',
        },
      });
      return;
  }
};
